import { Text, View, type StyleProp, type ViewStyle } from "react-native";
import { CircleAlert, Info, TriangleAlert } from "lucide-react-native";
import { colors, semanticColors, typography } from "../theme/tokens";

type Props = {
  children: string;
  /**
   * `error` for a problem with this input, `warning` for a value worth a second
   * look, `info` for a neutral note.
   */
  tone?: "error" | "warning" | "info";
  /** On the dark sign-in glass, where the light-surface colours can't be read. */
  onDark?: boolean;
  /** Centres it under a centred input, such as the code cells. */
  centered?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

/**
 * A message about one input, shown right under it: an icon and short text in
 * the tone's colour, with no box. Validation errors and field-specific
 * failures use it; anything about the screen or section as a whole is an
 * ActionBanner.
 */
export function FieldMessage({
  children,
  tone = "error",
  onDark = false,
  centered = false,
  testID,
  style,
}: Props) {
  const isError = tone === "error";
  const color = onDark
    ? isError
      ? semanticColors.onDark.danger
      : semanticColors.onDark.text.secondary
    : isError
      ? semanticColors.text.destructive
      : tone === "warning"
        ? semanticColors.status.warningText
        : colors.textLight;
  // A warning's triangle takes the warning yellow; its words the darker
  // shade of it that reads on cream (#3910).
  const iconColor =
    !onDark && tone === "warning" ? semanticColors.status.warning : color;
  const Icon = isError
    ? CircleAlert
    : tone === "warning"
      ? TriangleAlert
      : Info;

  return (
    <View
      testID={testID}
      accessibilityRole={isError ? "alert" : undefined}
      accessibilityLiveRegion={isError ? "polite" : undefined}
      style={[
        { flexDirection: "row", alignItems: "flex-start", gap: 6 },
        centered && { justifyContent: "center" },
        style,
      ]}
    >
      <Icon
        size={13}
        color={iconColor}
        strokeWidth={2}
        style={{ marginTop: 2 }}
      />
      <Text
        style={{
          ...(centered ? { flexShrink: 1, textAlign: "center" } : { flex: 1 }),
          fontFamily: typography.families.sans,
          fontSize: 12,
          lineHeight: 16,
          color,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

export default FieldMessage;
